import { chain, reduce, times, isNumber } from "lodash"
import { buildContributorKey } from "helpers/contributors"
import { isUnmodeled } from "helpers/securities"
import { generateGICSClassificationQueryForCompositionContributors } from "helpers/classification"
import { getDescriptorsQueryFragment, buildDescriptorsMap, mergeDescriptorsIntoChildren, BATCH_SIZE } from "./descriptors"
import { PORTFOLIO_QUERY, mergeAttributesIntoChildren } from "./attributes"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet, getIsNoLongerHeldFromContributor, makeUniverseFilter, getSourcePositionSet } from "../helpers"
import { getContributorLabel, NOT_HELD } from "../../connected-components/asset-view/helpers"

const MAX_DESCRIPTOR_BATCHES_PER_QUERY = 5
const COMPOSITION_PREFIX = "composition"
const BATCH_PREFIX = "batch"

const getContributorsSubquery = (classifications) => `id
    assetClass
    assetSubclass
    bloombergTicker
    description
    isUnmodeled
    modelProviderId
    internalId
    country
    currency
    economicExposure
    baseEconomicExposure
    positionSetEconomicExposure
    notHeld
    ${generateGICSClassificationQueryForCompositionContributors(classifications)}
  `

const getUngroupedContributorsSubquery = (classifications) => `positions {
  ${getContributorsSubquery(classifications)}
}`

function getGroupedContributorsSubQuery(groupByFields, classifications) {
  return `compositionBy(${getGroupBySubqueryParameters(groupByFields)}) {
  id
  description
  economicExposure
  positionSetEconomicExposure
  baseEconomicExposure
    contributors {
      ${getContributorsSubquery(classifications)}
    }
  }`
}

function getQuery(groupByFields, classifications, shouldUseIntradayQuery, { descriptorsFragment, attributesFragment }) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""
  const additionalParams = []
  if (descriptorsFragment) {
    additionalParams.push(", $filter: [SecuritySearchFilter!]!, $sort: [SecuritySearchSort], $currency: Currency")
  }
  if (attributesFragment) {
    additionalParams.push(", $portfolioId: String!")
  }
  const innerQuery = `
    composition (scaleFormat: $scaleFormat ${shouldUseIntradayQuery ? "" : ", includeNotHeld: $includeNotHeld"}) {
      gmv
      referenceEquity
      ${groupByFields ? getGroupedContributorsSubQuery(groupByFields, classifications) : getUngroupedContributorsSubquery(classifications)}
    }
  `

  if (shouldUseIntradayQuery) {
    return `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $scaleFormat: ScaleFormat!${extraParameters}, $batchId: Id${additionalParams.join("")}) {
      model (id: $modelId) {
        simulation:intradaySimulationBatch (positionSet: $positionSet, on: $date, batchId: $batchId) {
          ${innerQuery}
        }
        ${descriptorsFragment}
      }
      ${attributesFragment}
    }`
  }

  return `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $date: Date!, $scaleFormat: ScaleFormat!, $includeNotHeld: Boolean!${extraParameters}${additionalParams.join("")}) {
    model (id: $modelId) {
      simulation (positionSet: $positionSet, base: $base, from: $date, to: $date, interval: END_DATE) {
        ${innerQuery}
      }
      ${descriptorsFragment}
    }
      ${attributesFragment}
  }`
}

function parseContributors(prefix, contributors, options) {
  if (!contributors) {
    return {}
  }
  return contributors?.reduce((acc, contributor) => {
    if (contributor.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: {
              identifier: "Not Held",
              economicExposure: 0,
              positionSetEconomicExposure: 0,
              baseEconomicExposure: 0,
            },
          },
        }
      }

      acc[NOT_HELD].values[prefix].economicExposure += contributor.economicExposure
      acc[NOT_HELD].values[prefix].baseEconomicExposure += contributor.baseEconomicExposure
    } else {
      const [identifier, mic] = contributor.id.split(".")
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld,
        isNoLongerHeld: getIsNoLongerHeldFromContributor(contributor),
        isUnmodeled: isUnmodeled(contributor),
        values: {
          [prefix]: {
            internalId: contributor.internalId,
            modelProviderId: contributor.modelProviderId,
            identifier,
            mic,
            assetClass: contributor.assetClass,
            assetSubclass: contributor.assetSubclass,
            bloombergTicker: contributor.bloombergTicker,
            economicExposure: contributor.economicExposure,
            positionSetEconomicExposure: contributor.positionSetEconomicExposure,
            baseEconomicExposure: contributor.baseEconomicExposure,
            country: contributor.country,
            currency: contributor.currency,
            description: contributor.description,
            notHeld: contributor.notHeld,
            sector: contributor.sector?.name,
            industry: contributor.industry?.name,
          },
        },
      }
    }
    return acc
  }, {})
}

function parseGroupedContributors(prefix, groups, options) {
  if (!groups) {
    return {}
  }
  return chain(groups)
    .keyBy("id")
    .mapValues(group => ({
      label: group.description,
      values: {
        [prefix]: {
          identifier: group.description,
          economicExposure: group.economicExposure,
          positionSetEconomicExposure: group.positionSetEconomicExposure,
          baseEconomicExposure: group.baseEconomicExposure,
        },
      },
      children: parseContributors(prefix, group.contributors, options),
    }))
    .value()
}

function computeTotal(prefix, contributors, composition) {
  if (!composition) {
    return {}
  }
  const { gmv, referenceEquity } = composition
  return reduce(contributors, (acc, contributor) => {
    acc.economicExposure += contributor.values[prefix].economicExposure
    acc.positionSetEconomicExposure += contributor.values[prefix].positionSetEconomicExposure
    acc.baseEconomicExposure += contributor.values[prefix].baseEconomicExposure
    return acc
  }, {
    economicExposure: 0,
    positionSetEconomicExposure: 0,
    baseEconomicExposure: 0,
    gmv,
    referenceEquity,
  })
}

function calculateNumberOfQueries(shouldUseDescriptors, shouldUseAttributes, numberOfDescriptorQueries) {
  const needsNeitherDescriptorsNorAttributes = !(shouldUseAttributes || shouldUseDescriptors)

  const queriesCount = {
    composition: shouldUseDescriptors || needsNeitherDescriptorsNorAttributes ? 1 : 0,
    attributes: shouldUseAttributes && !shouldUseDescriptors ? 1 : 0,
  }

  if (numberOfDescriptorQueries > MAX_DESCRIPTOR_BATCHES_PER_QUERY) {
    queriesCount.composition = Math.ceil(numberOfDescriptorQueries / MAX_DESCRIPTOR_BATCHES_PER_QUERY)
  }

  return queriesCount
}

function determineFragments(iteration, shouldUseDescriptors, shouldUseAttributes, classifications, to, count) {
  const hasIteration = isNumber(iteration)

  const descriptorsFragment = shouldUseDescriptors && hasIteration
    ? getDescriptorsQueryFragment(classifications, to, count, iteration, MAX_DESCRIPTOR_BATCHES_PER_QUERY)
    : ""

  const shouldAttachAttributesToQuery =
    shouldUseAttributes && (
      (shouldUseDescriptors && iteration === 0)
      || (!shouldUseDescriptors && !hasIteration)
    )

  const attributesFragment = shouldAttachAttributesToQuery ? PORTFOLIO_QUERY : ""

  return {
    descriptorsFragment,
    attributesFragment,
    willUseDescriptors: descriptorsFragment !== "",
    willUseAttributes: attributesFragment !== "",
  }
}

function buildOptionalVariables(willUseDescriptors, willUseAttributes, filter, options, sanitizedPositionSet, currency) {
  const variables = {}

  if (willUseDescriptors) {
    Object.assign(variables, {
      filter,
      sort: options.sort,
      currency,
    })
  }

  if (willUseAttributes || willUseDescriptors || sanitizedPositionSet.type === "RULES") {
    Object.assign(variables, {
      portfolioId: getSourcePositionSet(sanitizedPositionSet).id,
    })
  }

  return variables
}

function collectDescriptorSecurities(data) {
  return chain(data.data.model)
    .flatMap((batch, key) => batch?.securities && key.startsWith(BATCH_PREFIX) ? batch.securities : null)
    .compact()
    .value()
}

function processDescriptors(data, children, groupBy, keySuffix) {
  const allSecurities = collectDescriptorSecurities(data)

  if (allSecurities.length > 0) {
    const descriptorsMap = buildDescriptorsMap(allSecurities)
    return mergeDescriptorsIntoChildren(children, descriptorsMap, groupBy, `descriptors${keySuffix}`)
  }

  return children
}

function processAttributes(data, children, groupById, keySuffix) {
  const portfolio = data?.data?.portfolio

  if (!portfolio?.dates?.length) {
    return children
  }

  return mergeAttributesIntoChildren(children, portfolio, groupById, keySuffix)
}

function parseChildren(prefix, data, groupBy, options) {
  const compositionData = data?.data?.model?.simulation?.composition?.[0]

  if (groupBy) {
    return parseGroupedContributors(prefix, compositionData?.compositionBy, options)
  }

  return parseContributors(prefix, compositionData?.positions, options)
}

export function createCompositionContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, classifications = [], useDescriptors, useAttributes, currency, count, keySuffix, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const { batchId, on, shouldUseIntradayQuery } = options

  const hasCompositionPrefix = prefix.startsWith(COMPOSITION_PREFIX)
  const shouldUseDescriptors = useDescriptors && hasCompositionPrefix
  const shouldUseAttributes = useAttributes && hasCompositionPrefix
  const numberOfDescriptorQueries = shouldUseDescriptors && count != null ? Math.ceil(count / BATCH_SIZE) : 0

  const { composition: numberOfCompositionQueries, attributes: numberOfAttributesQueries } = calculateNumberOfQueries(shouldUseDescriptors, shouldUseAttributes, numberOfDescriptorQueries)

  const filter = shouldUseDescriptors ? makeUniverseFilter(positionSet, base) : undefined

  function makeQuery(iteration) {
    const { descriptorsFragment, attributesFragment, willUseDescriptors, willUseAttributes } = determineFragments(iteration, shouldUseDescriptors, shouldUseAttributes, classifications, to, count)

    const optionalVariables = buildOptionalVariables(
      willUseDescriptors,
      willUseAttributes,
      filter,
      options,
      sanitizedPositionSet,
      currency,
    )

    return {
      prefix,
      key: ["composition", "contributors", prefix, modelId, positionSet, base, to, groupBy, groupById, tier, shouldUseIntradayQuery, batchId, on, filter, count, currency, iteration],
      query: getQuery(groupBy && groupByFields, classifications, shouldUseIntradayQuery, { descriptorsFragment, attributesFragment }),
      variables: {
        modelId,
        positionSet: sanitizedPositionSet,
        batchId,
        base,
        date: shouldUseIntradayQuery ? on : to,
        ...groupByFields,
        scaleFormat: options.scaleFormat || "PERCENT_EQUITY_GMV",
        includeNotHeld: !!base,
        ...optionalVariables,
      },
      select: (data) => {
        let children = parseChildren(prefix, data, groupBy, options)

        if (willUseDescriptors) {
          children = processDescriptors(data, children, !!groupBy, keySuffix)
        }

        if (willUseAttributes) {
          children = processAttributes(data, children, groupById, keySuffix)
        }

        const total = computeTotal(prefix, children, data.data.model.simulation.composition[0])

        return {
          label: "Total",
          values: {
            [prefix]: {
              ...total,
              identifier: "Total",
            },
          },
          children,
        }
      },
    }
  }

  const results = [
    ...times(numberOfAttributesQueries, () => makeQuery()),
    ...times(numberOfCompositionQueries, iteration => makeQuery(iteration)),
  ]
  return results
}
